import { resolveFieldWidget } from '@resource-kit/controllers';
import type { EntityField } from '@resource-kit/controllers/react';
import type { Widget } from '@resource-kit/model';
import { createWidgetCatalog, resolveWidget } from '@resource-kit/core';
import { FIELD_TYPE_REGISTRY, type FieldTypeSpec } from './field-type-registry';

/**
 * This application's binding of the widget catalog to the LEGACY field
 * (#1799, wired through in #1805).
 *
 * There is one catalog now — `@resource-kit/controllers`' widget registry — and
 * one resolution order inside it: a view item's `widget`, then the model field's
 * `widget`, then the field type's default. This module only says which of those
 * a legacy `EntityField` can supply, which is levels 2 and 3: legacy carries
 * ONE `fields[]` behind both the table and the form, so a per-view override has
 * nowhere to land (the adapter rejects it — see `checkItemIsLegacyCompatible`).
 */

/**
 * The widget that renders this field in a list or detail cell. `renderType` is
 * the field's own display override — a declaration that this column renders as
 * another type — so it selects the type default.
 */
export function resolveDisplayWidget<T>(field: EntityField<T>): Widget {
  return resolveFieldWidget({
    fieldWidget: field.widget,
    type: field.renderType ?? field.type,
  });
}

/**
 * The widget that edits this field in a form. Unlike the cell, the form always
 * follows the field's real `type`: `renderType` changes how a value is shown,
 * never what shape the operator may type into it.
 */
export function resolveInputWidget<T>(field: EntityField<T>): Widget {
  return resolveFieldWidget({ fieldWidget: field.widget, type: field.type });
}

/**
 * The field type's own cell projection, in the LEGACY `FieldTypeSpec` shape.
 *
 * Kept for `AuditTimeline`, which renders the old/new values of an audit diff.
 * That surface has no view item and no live record behind it — it deliberately
 * ignores the field's `render` override for the same reason — so it wants the
 * type projection, not a per-field widget. It also resolves DURING render, so
 * the returned object must have a stable identity across renders or the cell
 * subtree would remount on every tick; the registry row does, a freshly wrapped
 * component would not.
 */
const legacyCatalog = createWidgetCatalog<FieldTypeSpec>({
  typeDefaults: FIELD_TYPE_REGISTRY,
});

export function resolveCellWidget<T>(field: EntityField<T>): FieldTypeSpec {
  return resolveWidget(legacyCatalog, { type: field.renderType ?? field.type });
}
